<script setup lang="ts">
import { AppTypography } from '@/components';
import { comingSoonPages } from '@/enums/ComingSoon';
import { adminConfirmedPages } from '@/enums/AdminConfirmedPages';
import homeActive from '@/assets/img/nav/ic-home-active.svg';
import home from '@/assets/img/nav/ic-home.svg';
import notificationsActive from '@/assets/img/nav/ic-notifications-active.svg';
import notifications from '@/assets/img/nav/ic-notifications.svg';
import profileActive from '@/assets/img/nav/ic-profile-active.svg';
import profile from '@/assets/img/nav/ic-profile.svg';
import requestActive from '@/assets/img/nav/ic-request-active.svg';
import request from '@/assets/img/nav/ic-request.svg';
import searchActive from '@/assets/img/nav/ic-search-active.svg';
import search from '@/assets/img/nav/ic-search.svg';
import chatActive from '@/assets/img/nav/ic-chat-active.svg';
import chat from '@/assets/img/nav/ic-chat.svg';
import icPendingIcon from '@/assets/img/icon/profile/ic-pending.svg';
import { useAuthStore } from '@/modules/auth/store/auth';
import { storeToRefs } from 'pinia';
import { ComplateProfileDialog } from '@/core/components';

const authStore = useAuthStore();
const { user } = storeToRefs(authStore);
const { isFullyVerified } = storeToRefs(useAuthStore());

const emits = defineEmits<{
  (e: 'navigate'): void;
}>();

// Base navigation items that are common for all user types
const baseNavigations = [
  {
    title: 'Home',
    icon: home,
    activeIcon: homeActive,
    name: 'index',
    link: '/home',
    disabled: false,
  },
  {
    title: 'Coming Soon',
    icon: notifications,
    activeIcon: notificationsActive,
    link: { name: 'notifications' },
    disabled: true,
  }
];

// Computed navigation items that change based on user type
const navigations = computed(() => {
  const isWorker = user.value?.user_type === 'worker';
  
  // Define user-specific menu item based on type
  const userSpecificItem = isWorker
    ? {
        title: 'Jobs',
        icon: request,
        activeIcon: requestActive,
        link: { name: 'jobs', query: { tab: 'applied' } },
        disabled: false,
      }
    : {
        title: 'My Job Listings',
        icon: request,
        activeIcon: requestActive,
        link: { name: 'requests', query: { tab: 'myListing' } },
        disabled: false,
      };

  const clientSpecificItem = user.value?.user_type === 'client'
    ? {
        title: 'Search',
        icon: search,
        activeIcon: searchActive,
        link: { name: 'search' },
        disabled: false,
      }
    : null;
  
  // Add the user-specific item and profile at the end
  return [
    ...baseNavigations,
    ...(clientSpecificItem ? [clientSpecificItem] : []),
    userSpecificItem,
    {
      title: 'Chat',
      icon: chat,
      activeIcon: chatActive,
      name: 'chat',
      link: '/chat',
      disabled: false,
    },
    {
      title: 'Profile',
      icon: profile,
      activeIcon: profileActive,
      name: 'profile',
      link: '/my-profile',
      disabled: false,
    },
  ];
});

const route = useRoute();

const isActive = (nav): boolean => {
  return route.name.split('-')[0] === nav.link.name || route.name.split('-')[0] === nav?.name;
};

const dialog = ref(false);
const onClick = async (nav: any): Promise<void> => {
  if (adminConfirmedPages.includes(nav?.name || nav.link?.name) && !user.value?.profile_documents_verified) {
    dialog.value = true;
  } else {
    emits('navigate');
    await navigateTo(nav.link);
  }
};
</script>

<template>
  <v-list class="pa-4 pt-7" color="transparent">
    <v-list-item
      v-for="(nav, index) in navigations"
      :key="index"
      class="ga-4 d-flex align-center"
      @click="onClick(nav)"
      :active="isActive(nav)"
      :disabled="comingSoonPages.includes(nav.title.toLowerCase()) || nav.disabled"
    >
      <AppTypography type="body-2" class="d-flex align-center justify-space-between">
        <span>{{ nav.title }}</span>
        <img v-if="nav.title === 'Profile' && !isFullyVerified" :src="icPendingIcon" alt="Pending" class="ml-2" />
      </AppTypography>
      <slot :isComingSoon="comingSoonPages.includes(nav.title.toLowerCase())"></slot>

      <template #prepend>
        <slot name="prepend" :nav="nav" :isActive="isActive"> </slot>
      </template>
    </v-list-item>
  </v-list>
  <ComplateProfileDialog v-model="dialog" @close="dialog = false" />
</template>
